// Quality API: the Approval Register (CR-5534). Split out of quality.ts
// (500-line rule) and spread into qualityAPI, so callers keep using
// qualityAPI.getApprovalRegister(...) and the rest.
import api, { type AxiosResponse } from './client';
import type {
  AccreditationSubmission,
  SupplierAccreditation,
  ApprovalRegisterMatrix,
  QueryParams,
} from './types';

export const qualityApprovalsAPI = {
  // CR-5534: Quality Approval Register (Suppliers × Accreditations matrix +
  // CRUD on the supplier-accreditation bind table). The matrix endpoint
  // composes the full grid in one round trip; the CRUD endpoints back
  // the add/edit/delete dialog on each cell. The optional view params
  // (site / company / group) come from useView().getViewQueryParams()
  // and scope the matrix to the currently-picked customer in the top-bar.
  getApprovalRegister: (params?: Record<string, number | undefined>): Promise<AxiosResponse<ApprovalRegisterMatrix>> =>
    api.get('/quality/approval-register/', { params }),

  getSupplierAccreditations: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierAccreditation[] }>> =>
    api.get('/quality/supplier-accreditations/', { params }),

  getSupplierAccreditation: (id: number): Promise<AxiosResponse<SupplierAccreditation>> =>
    api.get(`/quality/supplier-accreditations/${id}/`),

  createSupplierAccreditation: (data: FormData): Promise<AxiosResponse<SupplierAccreditation>> =>
    api.post('/quality/supplier-accreditations/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateSupplierAccreditation: (id: number, data: FormData): Promise<AxiosResponse<SupplierAccreditation>> =>
    api.patch(`/quality/supplier-accreditations/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteSupplierAccreditation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/supplier-accreditations/${id}/`),

  /**
   * CR-7747: fetch an approval's certificate file through the authenticated
   * API. A plain link to the storage URL cannot carry the auth token and
   * 404s wherever /media/ is not served. 404 when nothing is attached or the
   * file has gone from storage.
   *
   * @param id - The supplier-accreditation (approval) id
   * @returns The file as a Blob; the response Content-Type is its MIME type
   */
  downloadSupplierAccreditationCertificate: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/quality/supplier-accreditations/${id}/download/`, { responseType: 'blob' }),

  // CR-7750: supplier-proposed approvals. The supplier writes here; the live
  // register row is only ever written by the customer's accept action, so an
  // unreviewed certificate never changes a RAG status.
  getAccreditationSubmissions: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: AccreditationSubmission[] }>> =>
    api.get('/quality/accreditation-submissions/', { params }),

  createAccreditationSubmission: (data: FormData): Promise<AxiosResponse<AccreditationSubmission>> =>
    api.post('/quality/accreditation-submissions/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateAccreditationSubmission: (id: number, data: FormData): Promise<AxiosResponse<AccreditationSubmission>> =>
    api.patch(`/quality/accreditation-submissions/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /** Supplier retracts their own pending submission. */
  withdrawAccreditationSubmission: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/accreditation-submissions/${id}/`),

  /**
   * Customer accepts a submission into the register.
   * @param force - re-send after a "live_changed" warning to overwrite values
   *   the customer edited after the supplier submitted.
   */
  acceptAccreditationSubmission: (id: number, reviewNotes = '', force = false): Promise<AxiosResponse<AccreditationSubmission>> =>
    api.post(`/quality/accreditation-submissions/${id}/accept/`, {
      review_notes: reviewNotes, force,
    }),

  /** Customer rejects a submission. A reason is required. */
  rejectAccreditationSubmission: (id: number, reviewNotes: string): Promise<AxiosResponse<AccreditationSubmission>> =>
    api.post(`/quality/accreditation-submissions/${id}/reject/`, {
      review_notes: reviewNotes,
    }),

  downloadAccreditationSubmissionCertificate: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/quality/accreditation-submissions/${id}/download/`, { responseType: 'blob' }),
};
